<script setup lang="ts">
/**
 * ResearchInstitutions —— 首页底部「科研机构」全宽区（F14）。
 *
 * 数据走 `SiteConfig.researchInstitutions: { name, url? }[]`（B16 后端契约），
 * **不是 Section**，故不经 Section Registry，由 HomePage 直接挂载在 grid 末尾。
 * 结构视觉对齐 `project/index.html` `.nyZzj`：红底标题条 + 白底卡片列表，
 * 机构项 pill 形（米黄底 + 暗红 → 箭头 + hover 上移改红底白字）。
 *
 * **全宽 Section**（Q7 决策，ADR-0015）：根级 CSS Modules `grid-column: 1 / -1`
 * 占满 HomePage 主容器 grid 整行，不参与 PC 端双列布局（同 F11 BannerSection 模式）。
 *
 * **渲染规则**（issue acceptance）：
 *   - 有 `url` → 外链 `<a :href target="_blank" rel="noopener">`（含 → 箭头 + hover 态）
 *   - 无 `url` → 纯文本 `<span>`，无 `<a>` 标签、无箭头、无 hover 翻转
 *   - 空 / null 列表 → 整区不渲染（`v-if` 控制，与「空机构本不该有」语义一致）
 */
import { copy } from '@/i18n/zh-CN';
import type { ResearchInstitutionView } from '@/api/types';

defineProps<{
  readonly institutions: readonly ResearchInstitutionView[];
}>();
</script>

<template>
  <section
    v-if="institutions.length > 0"
    id="institutions"
    :class="$style.institutions"
    data-section-type="institutions"
  >
    <div :class="$style.header" data-shell="nyZzj-dt">
      <h2 :class="$style.headerTitle">
        <img
          :class="$style.titleIcon"
          src="/template/i5.png"
          alt=""
          aria-hidden="true"
        >
        <span>{{ copy.sections.institutions.title }}</span>
      </h2>
      <img
        :class="$style.cornerIcon"
        src="/template/43.png"
        alt=""
        aria-hidden="true"
      >
    </div>
    <div :class="$style.body" data-shell="nyZzj-dd">
      <ul :class="$style.list">
        <li
          v-for="(item, index) in institutions"
          :key="`${item.name}-${index}`"
          :class="$style.item"
        >
          <a
            v-if="item.url"
            :class="$style.link"
            :href="item.url"
            target="_blank"
            rel="noopener"
          >{{ item.name }}</a>
          <span v-else :class="$style.name">{{ item.name }}</span>
        </li>
      </ul>
    </div>
  </section>
</template>

<style module>
/* 全宽 Section：占满 HomePage 主容器 grid 整行（Q7 / ADR-0015）。 */
.institutions {
  grid-column: 1 / -1;
  margin-bottom: var(--space-5);
}

/* 模板 .nyZzj dl dt：红底标题条。 */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--template-institutions-header-height);
  padding: var(--space-2) var(--space-4) 0 var(--space-6);
  background: var(--color-brand-primary) url('/template/135.png') no-repeat left center;
  background-size: auto 100%;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.headerTitle {
  display: flex;
  align-items: center;
  margin: 0;
  color: var(--color-bg-primary);
  font-size: var(--template-institutions-title-size);
  font-weight: 700;
}

.titleIcon {
  height: var(--template-institutions-title-icon-height);
  margin-right: var(--template-title-icon-gap);
}

.cornerIcon {
  height: var(--template-institutions-corner-icon-height);
}

/* 模板 .nyZzj dl dd：白底卡片容器。 */
.body {
  padding: var(--space-5);
  background: var(--color-bg-secondary);
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
  box-shadow: var(--shadow-card);
}

/* 模板 .list：flex wrap 等宽三列。 */
.list {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.item {
  width: var(--template-institutions-item-width);
  margin-bottom: var(--space-3);
}

/* 模板 .list li a：pill 形链接。链接与纯文本共用药丸底，仅链接带箭头 + hover 翻转。 */
.link,
.name {
  display: flex;
  align-items: center;
  height: var(--template-institutions-item-height);
  padding: 0 var(--space-4);
  padding-right: var(--template-institutions-item-arrow-gap);
  background: var(--template-institutions-item-bg);
  border-radius: var(--template-institutions-item-radius);
  box-shadow: var(--shadow-card);
  color: var(--color-brand-primary);
  font-size: var(--template-institutions-item-font);
}

.link {
  position: relative;
  transition:
    background var(--template-hover-duration) ease,
    color var(--template-hover-duration) ease,
    transform var(--template-hover-duration) ease;
}

/* 模板 .list li a::after：右侧 → 箭头。 */
.link::after {
  content: "→";
  position: absolute;
  right: var(--space-2);
  top: 50%;
  transform: translateY(-50%);
  color: var(--template-institutions-arrow);
  font-size: var(--font-size-lg);
  font-weight: 700;
  transition:
    right var(--template-hover-duration) ease,
    color var(--template-hover-duration) ease;
}

/* 模板 .list li a:hover：红底白字 + 上移 + 箭头跟进。 */
.link:hover {
  background: var(--color-brand-primary);
  color: var(--color-bg-primary);
  transform: translateY(var(--template-institutions-hover-lift));
}

.link:hover::after {
  right: var(--space-1);
  color: var(--color-bg-primary);
}
</style>
